import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, setFilters } from '../redux/slices/usersSlice';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE } from '../utils/paginationUtils';
import Grid from '@mui/material/Grid';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import UsersDrawer from '../components/UsersDrawer';
import NoData from '../components/NoData';

function Users() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [openCreate, setOpenCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const isInitialMount = useRef(true);

  const usersData = useSelector(({ users: { data } }) => data);
  const usersDataError = useSelector((state) => state.users.error);
  const totalItems = useSelector((state) => state.users.totalItems);
  const [searchParams, setSearchParams] = useSearchParams();

  const options = useSelector((state) => ({
    ...state.users.options,
  }));

  const handleChangePage = (_, newPage) => {
    dispatch(setFilters({ page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    dispatch(setFilters({ limit: newLimit, page: 1 }));
  };

  const onDrawerClose = () => {
    setOpenCreate(false);
    setEditUser(null);
  };

  const onCreateOpen = () => {
    setOpenCreate(true);
  };

  const onUpdateOpen = (user) => {
    setEditUser(user);
  };

  useEffect(() => {
    const filtersFromUrl = {
      page: parseInt(searchParams.get('page')) || DEFAULT_PAGE,
      limit: parseInt(searchParams.get('limit')) || DEFAULT_ROWS_PER_PAGE,
      search: searchParams.get('search') || '',
    };

    dispatch(setFilters(filtersFromUrl));
    dispatch(fetchUsers(filtersFromUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchParams.get('page'), searchParams.get('limit')]);

  useEffect(() => {
    // Skip on initial mount since first useEffect already handles it
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setSearchParams(
      {
        page: String(options.page),
        limit: String(options.limit),
        search: options.search || '',
      },
      { replace: true }
    );
    dispatch(fetchUsers(options));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.page, options.limit]);

  const handleOnSearchClick = () => {
    const newFilters = {
      page: 1,
      limit: options.limit,
      search: options.search || '',
    };

    dispatch(setFilters(newFilters));
    dispatch(fetchUsers(newFilters));

    setSearchParams({
      page: 1,
      limit: String(options.limit),
      search: options.search || '',
    });
  };

  useEffect(() => {
    if (usersDataError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [usersDataError, t]);

  return (
    <div className="users">
      <Grid sx={{ mb: 4 }} container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h3">{t('Users')}</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={onCreateOpen}>
            {t('Add user')}
          </Button>
        </Grid>
      </Grid>
      <Paper sx={{ p: 1, mb: 2 }}>
        <Grid item sx={{ width: 400, ml: 'auto' }}>
          <TextField
            onChange={(event) => dispatch(setFilters({ search: event.target.value }))}
            value={options.search}
            sx={{ marginRight: 4 }}
            inputProps={{ autoComplete: 'off' }}
            label={t('Search')}
            fullWidth
            placeholder={t('Search by email, last name or first name')}
            variant="outlined"
            type="text"
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleOnSearchClick} edge="end">
                  <SearchOutlined />
                </IconButton>
              ),
            }}
          />
        </Grid>
      </Paper>

      {usersData.length ? (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">{t('Created')}</TableCell>
                  <TableCell align="left">{t('Email')}</TableCell>
                  <TableCell align="left">{t('First Name')}</TableCell>
                  <TableCell align="left">{t('Last Name')}</TableCell>
                  <TableCell align="left">{t('Role')}</TableCell>
                  <TableCell align="left">{t('Status')}</TableCell>
                  <TableCell align="right">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersData.map(({ id, created_at, email, first_name, last_name, role, status }, index) => (
                  <TableRow
                    key={id}
                    sx={{
                      textAlign: 'right',
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                      '&:nth-of-type(odd)': (theme) => ({
                        backgroundColor: theme.palette.action.hover,
                      }),
                    }}
                  >
                    <TableCell>{dayjs(created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell>{first_name}</TableCell>
                    <TableCell>{last_name}</TableCell>
                    <TableCell>{role}</TableCell>
                    <TableCell>
                      {status === 'ACTIVE' ? (
                        <Chip
                          sx={{ mt: 1, borderRadius: 1 }}
                          size="small"
                          label={`${status}`}
                          variant="outlined"
                          color="success"
                        />
                      ) : (
                        <Chip
                          sx={{ mt: 1, borderRadius: 1 }}
                          size="small"
                          label={`${status}`}
                          variant="outlined"
                          color="error"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" aria-label="update" onClick={() => onUpdateOpen(usersData[index])}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={options.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={options.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE}
            labelRowsPerPage={t('rowsPaginator')}
          />
        </Paper>
      ) : (
        <NoData />
      )}
      <UsersDrawer open={openCreate || !!editUser} user={editUser} onClose={onDrawerClose} />
    </div>
  );
}

export default Users;
